import { useState } from 'react'
import { Modal, Stack, Button, Alert, Textarea } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconInfoCircle } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { organizationsApi, type OrgUser } from '@/api/organizations'
import { formatApiError } from '@/api/errors'

// Guards de férias do backend (spec §3.3) em PT-BR — férias têm efeito global
// na conta, então há mais recusas possíveis que nas outras ações do gestor.
const LEAVE_ERROR_MESSAGES: Record<string, string> = {
  cannot_leave_system_account: 'Contas de sistema (bots) não podem entrar de férias.',
  cannot_leave_admin: 'Administradores da plataforma não podem entrar de férias — use a desativação.',
  cannot_leave_org_manager: 'Este colaborador é gestor/responsável de uma Organização. Apenas administradores podem afastá-lo.',
  gestor_cannot_manage_gestor: 'Apenas administradores podem gerenciar gestores/responsável.',
  invalid_leave_until: 'Data inválida: escolha uma data futura, no máximo 12 meses à frente.',
  user_not_in_org: 'Este colaborador não pertence mais a esta Organização.',
}

function formatLeaveError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && LEAVE_ERROR_MESSAGES[detail]) return LEAVE_ERROR_MESSAGES[detail]
  return formatApiError(err, fallback)
}

/** Teto de 12 meses — espelha MAX_LEAVE_DAYS do backend (não deixa o usuário
 *  escolher uma data que o servidor vai recusar com 400). */
const MAX_LEAVE_DAYS = 366

/**
 * Modal de férias de um colaborador (spec 2026-07-24 §3.3).
 *
 * O acesso ao ConsoleAI fica suspenso até a data e o retorno é **automático**
 * (sem cron, sem precisar desmarcar). As sessões abertas são encerradas na
 * hora, então o modal avisa antes de confirmar.
 */
export function LeaveUserModal({
  orgId, user, opened, onClose,
}: {
  orgId: number
  user: OrgUser | null
  opened: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [until, setUntil] = useState<Date | null>(null)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const reset = () => { setUntil(null); setNote(''); setSubmitting(false) }
  const handleClose = () => { reset(); onClose() }

  const handleSubmit = async () => {
    if (!user || !until) return
    setSubmitting(true)
    try {
      await organizationsApi.setOrgUserLeave(
        orgId, user.user_id, dayjs(until).format('YYYY-MM-DD'), note.trim() || undefined,
      )
      qc.invalidateQueries({ queryKey: ['org-users', orgId] })
      notifications.show({
        title: 'Férias registradas',
        message: `${user.full_name || user.email} fica sem acesso até ${dayjs(until).format('DD/MM/YYYY')}`,
        color: 'blue',
      })
      handleClose()
    } catch (err) {
      notifications.show({ title: 'Erro', message: formatLeaveError(err, 'Falha ao registrar férias'), color: 'red' })
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={user ? `Férias de ${user.full_name || user.email}` : 'Férias do colaborador'}
    >
      <Stack gap="sm">
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          O acesso ao ConsoleAI fica suspenso até a data escolhida e as sessões
          abertas são encerradas agora. O retorno é automático — não precisa
          desmarcar depois.
        </Alert>
        <DateInput
          label="Férias até"
          placeholder="AAAA-MM-DD"
          value={until}
          onChange={setUntil}
          valueFormat="YYYY-MM-DD"
          minDate={dayjs().add(1, 'day').toDate()}
          maxDate={dayjs().add(MAX_LEAVE_DAYS, 'day').toDate()}
          withAsterisk
        />
        <Textarea
          label="Observação (opcional)"
          placeholder="Registro interno (fica na auditoria)"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={2}
        />
        <Button onClick={handleSubmit} loading={submitting} disabled={!until}>
          Confirmar férias
        </Button>
      </Stack>
    </Modal>
  )
}
